import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import PlanTaskWidget, { stepsDone as stepsDoneCount, type TaskWidget } from './components/PlanTaskWidget'
import RevisionSubmitWidget from './components/RevisionSubmitWidget'
import {
  plan_top_down_path,
  plan_top_down_submit_path,
  plan_top_down_review_path,
  plan_top_down_by_channel_quarterly_path,
  plan_top_down_update_channel_quarter_targets_path,
  plan_top_down_complete_overall_path,
  plan_top_down_complete_quarterly_path,
} from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import StepProgress from './components/StepProgress'
import PeriodPicker from './components/PeriodPicker'
import EditorHeader, { DraftActivityBanner, RevisionBanner } from './components/EditorHeader'
import { formatStatDate } from './components/HeaderStat'
import PlanSwitcher, { type PlanSwitcherData } from './components/PlanSwitcher'
import QuarterlyMatrixHead from './components/QuarterlyMatrixHead'
import QuarterlyMatrixRow from './components/QuarterlyMatrixRow'
import RevisionRequestBanner from './components/RevisionRequestBanner'
import { type EditorRequest, type RequestComment } from './components/revisionRequest'
import { useRowNotes } from './components/useRowNotes'
import { revisionLabels } from './components/periodLabel'

interface Channel { id: string; name: string; slug: string }
interface Quarter { label: string; start_date: string; end_date: string }
type CellMap = Record<string, Record<string, number>> // channel_id -> quarter_start -> value

interface Props {
  slug: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    overall_completed_at: string | null
    channel_step_completed_at: string | null
    quarterly_step_completed_at: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  begin_date: string
  end_date: string
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  plan_switcher: PlanSwitcherData
  // Every open planning task the viewer is a PIC of (Plan::CycleTaskProps); empty for everyone else.
  task_widgets: TaskWidget[]
  // Published plan with no planning task open: the Submit-for-review control.
  revision_submit: boolean
  channels: Channel[]
  past_quarters: Quarter[]
  plan_quarters: Quarter[]
  // Quarters already covered by actual sales. Empty for a plain version.
  actual_quarters: Quarter[]
  past: Record<string, Record<string, string>>
  plan: Record<string, Record<string, number>>
  actual: Record<string, Record<string, string>>
  channel_targets: Record<string, number>
  // The draft this page edits; its id rides along with every autosave.
  version: { id: string } | null
  // The draft is a revision of an already-published plan.
  revision: boolean
  // Someone else's unsubmitted edits in the shared draft (server omits it
  // when the viewer made the last edit).
  draft_activity?: { by: string; at: string } | null
  // The open sent request on this plan, this page's comments only. Null
  // when there is no open request.
  revision_request: EditorRequest | null
  review_comments?: RequestComment[]
}

function formatPeriod(begin_date: string, end_date: string): string {
  const fmt = (d: string) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${fmt(begin_date)} – ${fmt(end_date)}`
}

export default function ByChannelQuarterly({
  slug,
  top_down_plan,
  begin_date,
  end_date,
  period,
  plan_period,
  actuals_months,
  plan_switcher,
  channels,
  past_quarters,
  plan_quarters,
  actual_quarters,
  past,
  plan,
  actual,
  channel_targets,
  version,
  revision,
  draft_activity,
  task_widgets,
  revision_submit,
  revision_request,
  review_comments,
}: Props) {
  const market = useMarket()
  const currency = top_down_plan.currency
  const rowNotes = useRowNotes(revision_request, review_comments)
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // Full year = actual sales (every channel, every actual quarter) + this
  // version's plan target.
  const fullYearTarget = useMemo(() => {
    const actualTotal = Object.values(actual).reduce(
      (sum, row) => sum + Object.values(row).reduce((s, v) => s + (Number(v) || 0), 0),
      0
    )
    return actualTotal + top_down_plan.revenue_target
  }, [actual, top_down_plan.revenue_target])
  const [overallCompletedAt] = useState<string | null>(top_down_plan.overall_completed_at)
  const [channelCompletedAt] = useState<string | null>(top_down_plan.channel_step_completed_at)
  const [quarterlyCompletedAt, setQuarterlyCompletedAt] = useState<string | null>(top_down_plan.quarterly_step_completed_at)
  const [planValues, setPlanValues] = useState<CellMap>(() => structuredClone(plan))
  const [saveStatus, setSaveStatus] = useState<{ saving: boolean; savedAt: Date | null }>({ saving: false, savedAt: null })
  const dirtyRef = useRef<Map<string, { org_node_id: string; quarter_start_date: string; target_revenue: number }>>(new Map())
  const flushTimerRef = useRef<number | null>(null)

  const flush = useCallback(async () => {
    if (dirtyRef.current.size === 0) return
    const cells = Array.from(dirtyRef.current.values())
    dirtyRef.current.clear()
    setSaveStatus({ saving: true, savedAt: null })
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    try {
      const res = await fetch(plan_top_down_update_channel_quarter_targets_path(market.slug, slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ version_id: version?.id ?? null, cells })
      })
      if (res.ok) {
        const data = await res.json().catch(() => null)
        setSaveStatus({ saving: false, savedAt: new Date() })
        if (data && 'quarterly_step_completed_at' in data) {
          setQuarterlyCompletedAt(data.quarterly_step_completed_at)
        }
      } else {
        setSaveStatus({ saving: false, savedAt: null })
        const data = await res.json().catch(() => ({ error: 'Failed to save.' }))
        window.alert(data.error ?? 'Failed to save.')
      }
    } catch {
      setSaveStatus({ saving: false, savedAt: null })
    }
  }, [slug, market.slug, version?.id])

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current != null) window.clearTimeout(flushTimerRef.current)
    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null
      void flush()
    }, 500)
  }, [flush])

  const handleCellChange = (channelId: string, quarterStart: string, next: number) => {
    setPlanValues((prev) => {
      const prevRow = prev[channelId] ?? {}
      const nextRow: Record<string, number> = { ...prevRow, [quarterStart]: next }
      const idx = plan_quarters.findIndex((q) => q.start_date === quarterStart)
      if (idx >= 0) {
        for (let k = idx + 1; k < plan_quarters.length; k++) {
          const prevQ = plan_quarters[k - 1].start_date
          const curQ = plan_quarters[k].start_date
          const oldBase = Number(prevRow[prevQ] ?? 0)
          const oldValue = Number(prevRow[curQ] ?? 0)
          const newBase = Number(nextRow[prevQ] ?? 0)
          let newValue = oldValue
          if (oldBase > 0) {
            const growth = (oldValue - oldBase) / oldBase
            newValue = Math.max(0, Math.round(newBase * (1 + growth)))
          }
          if (newValue !== oldValue) {
            nextRow[curQ] = newValue
            dirtyRef.current.set(`${channelId}:${curQ}`, {
              org_node_id: channelId,
              quarter_start_date: curQ,
              target_revenue: newValue
            })
          }
        }
      }
      return { ...prev, [channelId]: nextRow }
    })
    dirtyRef.current.set(`${channelId}:${quarterStart}`, {
      org_node_id: channelId,
      quarter_start_date: quarterStart,
      target_revenue: next
    })
    scheduleFlush()
  }

  const handleCompleteOverall = async () => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    await fetch(plan_top_down_complete_overall_path(market.slug, slug), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
      credentials: 'same-origin'
    })
  }

  const handleCompleteQuarterly = async () => {
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    const res = await fetch(plan_top_down_complete_quarterly_path(market.slug, slug), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
      credentials: 'same-origin'
    })
    if (res.ok) {
      const data = await res.json()
      setQuarterlyCompletedAt(data.quarterly_step_completed_at)
    } else {
      const data = await res.json().catch(() => ({ error: 'Failed to complete quarterly breakdown.' }))
      window.alert(data.error ?? 'Failed to complete quarterly breakdown.')
    }
  }

  const saveLabel = saveStatus.saving
    ? 'Saving…'
    : saveStatus.savedAt
      ? `Saved ${saveStatus.savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : ''

  const done = stepsDoneCount(overallCompletedAt, channelCompletedAt, quarterlyCompletedAt)

  return (
    <AppLayout
      header={<EditorHeader
        name={top_down_plan.name}
        beginDate={begin_date}
        endDate={end_date}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        editPlanHref={plan_top_down_path(market.slug, slug)}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
      headerActions={
        task_widgets.length > 0
          ? (
            <div className="flex flex-col gap-1.5">
              {task_widgets.map((w) => (
                <PlanTaskWidget
                  key={w.id}
                  task={w}
                  done={done}
                  submitHref={plan_top_down_submit_path(market.slug, slug, { task_id: w.id })}
                  reviewHref={plan_top_down_review_path(market.slug, slug, { task_id: w.id })}
                />
              ))}
            </div>
          )
          : revision_submit && <RevisionSubmitWidget done={done} submitHref={plan_top_down_submit_path(market.slug, slug)} />
      }
    >
      <Head title={`${top_down_plan.name} — Quarterly`} />
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-4 py-2.5"
        >
          <PlanSwitcher data={plan_switcher} />
        </div>
        <div className="min-w-0 flex-1 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm leading-relaxed text-foreground">
          {periodLabels ? (
            <>Break down each channel's remaining-months target ({periodLabels.plan}) by quarter. Quarters already past show actual sales.</>
          ) : (
            <>
              Break down each channel's target revenue by quarter in {top_down_plan.org_node?.name ?? market.name} from {formatStatDate(begin_date)} – {formatStatDate(end_date)}.<br />
              After you finish, hit Complete button.
            </>
          )}
        </div>
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-4 py-2.5 ml-auto"
        >
          <PeriodPicker
            planId={top_down_plan.id}
            beginDate={begin_date}
            endDate={end_date}
            start={period.start}
            end={period.end}
            path={plan_top_down_by_channel_quarterly_path(market.slug, slug)}
            only={['period', 'past', 'past_quarters']}
          />
        </div>
      </div>
      <StepProgress
        slug={slug}
        currentStep="quarterly"
        overallCompletedAt={overallCompletedAt}
        channelCompletedAt={channelCompletedAt}
        quarterlyCompletedAt={quarterlyCompletedAt}
        onCompleteOverall={handleCompleteOverall}
        onCompleteQuarterly={handleCompleteQuarterly}
        quarterlyStatusLabel={saveLabel}
      />
      {revision && <RevisionBanner />}
      {revision_request && <RevisionRequestBanner request={revision_request} section="quarterly" slug={slug} />}
      {draft_activity && <DraftActivityBanner activity={draft_activity} />}

      <div className="border rounded-lg" style={{ background: 'var(--background)', borderColor: 'var(--border)' }}>
        <div className="overflow-x-auto">
        {/* Fixed layout: Channel and Target keep set widths; the rest is split
            into equal columns, one per past/actual quarter and two per Plan
            quarter (its cells span two). The minimum keeps a Plan quarter at
            228px so its inputs never squeeze; the wrapper scrolls instead. */}
        <table
          className="text-xs border-collapse w-full"
          style={{
            tableLayout: 'fixed',
            minWidth: 88 + 96 + 114 * (past_quarters.length + actual_quarters.length + 2 * plan_quarters.length),
          }}
        >
          <QuarterlyMatrixHead
            pastQuarters={past_quarters}
            actualQuarters={actual_quarters}
            planQuarters={plan_quarters}
          />
          <tbody>
            {channels.map((c) => (
              <QuarterlyMatrixRow
                key={c.id}
                channel={c}
                pastQuarters={past_quarters}
                actualQuarters={actual_quarters}
                planQuarters={plan_quarters}
                past={past[c.id] ?? {}}
                actual={actual[c.id] ?? {}}
                planValues={planValues[c.id] ?? {}}
                target={channel_targets[c.id] ?? 0}
                currency={currency}
                onCellChange={(quarterStart, next) => handleCellChange(c.id, quarterStart, next)}
                notes={rowNotes[c.id]}
              />
            ))}
            {channels.length === 0 && (
              <tr>
                <td colSpan={2 + past_quarters.length + actual_quarters.length + 2 * plan_quarters.length} className="px-3 py-6 text-center" style={{ color: 'var(--muted-foreground)' }}>
                  No channel (leaf) org nodes found under this plan&apos;s scope.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </AppLayout>
  )
}
